/**
 * Category Styles - Single Source of Truth
 *
 * All UI display properties for drama score categories.
 * Used by: animation badge reveal, results page score card, and any future category display.
 *
 * Category thresholds are defined in packages/shared/src/constants/drama.ts (0-10 scale).
 * This file provides the UI layer: gradients, labels, icons, and colors for each category.
 *
 * 4-category system (0-10 scale):
 * - safe          (0-2.0):   green
 * - caution       (2.1-5.0): amber
 * - high_drama    (5.1-7.0): red
 * - extreme_drama (7.1-10):  purple
 */

export interface CategoryStyle {
  /** Solid background color */
  bg: string;
  /** [start, end] colors for LinearGradient */
  gradient: readonly [string, string];
  /** Text color (WCAG compliant contrast) */
  text: string;
  /** Display label */
  label: string;
  /** Icon/emoji */
  icon: string;
}

/**
 * Primary 4-category styles + safety (circuit breaker) + legacy aliases.
 *
 * To add a new category: add it here and all consumers update automatically.
 */
export const CATEGORY_STYLES: Record<string, CategoryStyle> = {
  // === Primary 4-category system ===
  safe: {
    bg: '#065F46',
    gradient: ['#10B981', '#059669'] as const,
    text: '#FFFFFF',
    label: 'Low Drama',
    icon: '✓',
  },
  caution: {
    bg: '#92400E',
    gradient: ['#F59E0B', '#D97706'] as const,
    text: '#1F2937',
    label: 'Caution',
    icon: '⚠️',
  },
  high_drama: {
    bg: '#991B1B',
    gradient: ['#EF4444', '#DC2626'] as const,
    text: '#FFFFFF',
    label: 'High Drama',
    icon: '🔥',
  },
  extreme_drama: {
    bg: '#5B21B6',
    gradient: ['#7C3AED', '#5B21B6'] as const,
    text: '#FFFFFF',
    label: 'Extreme Drama',
    icon: '💥',
  },

  // === Safety category (circuit breaker, score = null) ===
  safety: {
    bg: '#1E40AF',
    gradient: ['#3B82F6', '#1D4ED8'] as const,
    text: '#FFFFFF',
    label: 'Support Available',
    icon: '💙',
  },

  // === Legacy aliases (backend may return these directly) ===
  low: {
    bg: '#065F46',
    gradient: ['#10B981', '#059669'] as const,
    text: '#FFFFFF',
    label: 'Low Drama',
    icon: '✓',
  },
  medium: {
    bg: '#92400E',
    gradient: ['#F59E0B', '#D97706'] as const,
    text: '#1F2937',
    label: 'Caution',
    icon: '⚠️',
  },
  high: {
    bg: '#991B1B',
    gradient: ['#EF4444', '#DC2626'] as const,
    text: '#FFFFFF',
    label: 'High Drama',
    icon: '🔥',
  },
  extreme: {
    bg: '#5B21B6',
    gradient: ['#7C3AED', '#5B21B6'] as const,
    text: '#FFFFFF',
    label: 'Extreme Drama',
    icon: '💥',
  },
  // Additional legacy aliases
  moderate: {
    bg: '#92400E',
    gradient: ['#F59E0B', '#D97706'] as const,
    text: '#1F2937',
    label: 'Caution',
    icon: '⚠️',
  },
  risky: {
    bg: '#991B1B',
    gradient: ['#EF4444', '#DC2626'] as const,
    text: '#FFFFFF',
    label: 'High Drama',
    icon: '🔥',
  },
  critical: {
    bg: '#5B21B6',
    gradient: ['#7C3AED', '#5B21B6'] as const,
    text: '#FFFFFF',
    label: 'Extreme Drama',
    icon: '💥',
  },
};

/** Default fallback category when lookup fails */
export const FALLBACK_CATEGORY = CATEGORY_STYLES.caution;

/**
 * Maps drama score (0-10 scale) to category key.
 *
 * Aligned with packages/shared/src/constants/drama.ts:
 * - SAFE_MAX: 2.0, CAUTION_MAX: 5.0, HIGH_DRAMA_MAX: 7.0
 *
 * @param score - Drama score on 0-10 scale, or null/undefined for circuit breaker
 * @returns Category key for CATEGORY_STYLES lookup
 */
export function getCategoryFromScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return 'safety';
  if (score <= 2) return 'safe';
  if (score <= 5) return 'caution';
  if (score <= 7) return 'high_drama';
  return 'extreme_drama';
}

/**
 * Get category style with safe fallback.
 * Prefer this over raw CATEGORY_STYLES[key] to avoid undefined.
 */
export function getCategoryStyle(category: string): CategoryStyle {
  return CATEGORY_STYLES[category] || FALLBACK_CATEGORY;
}
